import { ref } from 'vue'

import { handleFileUpload } from '@backmarket/front-upload'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { DocumentStatusEnum } from '@ds/components/Document/Document.constant'

import type { AttachmentUpload } from '../types'

import {
  POLL_UPLOAD_CANCELLED,
  POLL_UPLOAD_FAILED,
} from './useAttachmentUpload.constants'
import translations from './useAttachmentUpload.translations'
import { usePollUploadMetadata } from './usePollUploadMetadata'

export interface UseAttachmentUploadOptions {
  orderlinePublicId: string
  onAttachmentUpdate?: (attachments: AttachmentUpload[]) => void
}

export function useAttachmentUpload(options: UseAttachmentUploadOptions) {
  const { orderlinePublicId, onAttachmentUpdate } = options

  const i18n = useI18n()
  const { pollUploadMetadata } = usePollUploadMetadata()

  async function uploadAttachments(
    attachmentsToUpload: AttachmentUpload[],
    currentAttachments: AttachmentUpload[],
    abortSignal?: AbortSignal,
  ) {
    // Add all attachments to the list immediately with loading status
    const currentState = ref([
      ...currentAttachments,
      ...attachmentsToUpload.map((attachment) => ({
        ...attachment,
        status: DocumentStatusEnum.Loading,
      })),
    ])

    if (onAttachmentUpdate) {
      onAttachmentUpdate(currentState.value)
    }

    // Helper function to update attachment state
    const updateAttachmentState = (
      attachment: AttachmentUpload,
      updates: Partial<AttachmentUpload>,
    ) => {
      if (onAttachmentUpdate) {
        currentState.value = currentState.value.map((prev) =>
          prev.file.name === attachment.file.name &&
          prev.file.size === attachment.file.size
            ? { ...prev, ...updates }
            : prev,
        )
        onAttachmentUpdate(currentState.value)
      }
    }

    // Upload all attachments concurrently
    const uploadPromises = attachmentsToUpload.map(async (attachment) => {
      try {
        const [uploadResult] = await uploadFilesAndGetMetadata(
          [attachment.file],
          abortSignal,
        )

        updateAttachmentState(attachment, {
          status: DocumentStatusEnum.Default,
          handle: uploadResult.handle,
          bucketName: uploadResult.bucketName,
          filePath: uploadResult.filePath,
        })
      } catch (error) {
        updateAttachmentState(attachment, {
          status: DocumentStatusEnum.Error,
          errorMessage: error instanceof Error ? error.message : undefined,
        })
      }
    })

    // Wait for all uploads to complete
    await Promise.all(uploadPromises)
  }

  async function uploadFilesAndGetMetadata(
    files: File[],
    abortSignal?: AbortSignal,
  ): Promise<Pick<AttachmentUpload, 'handle' | 'bucketName' | 'filePath'>[]> {
    if (abortSignal?.aborted) {
      throw new Error(POLL_UPLOAD_CANCELLED)
    }

    const uploadPromises = files.map(async (file) => {
      const uploadResponse = await handleFileUpload(
        `${getHttpBaseUrl()}/services/uploads`,
        'care_hrm_attachment',
        { orderlinePublicId },
        file,
      )

      const { status, errors, handle } = uploadResponse
      const logger = useLogger()

      if (status !== 'success') {
        logger.error(
          `Upload preflight error: ${errors[0].message} for field ${errors[0].field}`,
          {
            owners: ['bot-squad-seller-care-front'],
          },
        )

        throw new Error(
          errors[0]?.message ||
            i18n(translations.errorFailedToUploadFile, { filename: file.name }),
        )
      }

      if (abortSignal?.aborted) {
        throw new Error(POLL_UPLOAD_CANCELLED)
      }

      const uploadMetadata = await pollUploadMetadata(handle, abortSignal)
      const {
        data: {
          bucket_name: bucketName,
          file_path: filePath,
          status: pollUploadStatus,
          status_reason: statusReason,
        },
      } = uploadMetadata

      if (pollUploadStatus === 'failed') {
        logger.error(`Upload polling error: ${statusReason}`, {
          owners: ['bot-squad-seller-care-front'],
        })
        throw new Error(
          i18n(translations.errorFailedToUploadFile, { filename: file.name }),
          {
            cause: POLL_UPLOAD_FAILED,
          },
        )
      }

      return {
        handle,
        filePath,
        bucketName,
      }
    })

    return Promise.all(uploadPromises)
  }

  return {
    uploadAttachments,
    uploadFilesAndGetMetadata,
  }
}
